import type { ReactNode } from 'react';
import { Component } from 'react';
import { Button } from './ui';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * ErrorBoundary Component
 *
 * Catches JavaScript errors anywhere in the child component tree, logs those errors,
 * and displays a fallback UI instead of the component tree that crashed.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  override render() {
    if (this.state.hasError) {
      return (
        <div className='flex min-h-screen items-center justify-center bg-brand-25 p-4'>
          <div className='max-w-md rounded-lg border border-red-200 bg-white p-6 text-center shadow-lg'>
            <h2 className='mb-2 text-xl font-semibold text-red-550'>Something went wrong</h2>
            <p className='mb-4 text-sm text-gray-500'>
              {this.state.error?.message || 'An unexpected error occurred'}
            </p>
            <Button onClick={this.handleReset} variant='outline'>
              Try Again
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
